import React, { useState, useEffect } from 'react';
import { Link } from '@superops/ui-design-system';
import DefaultViewer from './DefaultViewer';
import ImageViewer from './ImageViewer';
import { CustomModal, SuperPdfViewer, getHeaderValue } from '@nile/components';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';

/**
 *
 * Attachment Previewer - shows preview of different attachment types
 *
 * const sampleList = [
 *  {
 *    name: 'Attachment name',
 *    url: 'https://sample.pdf'
 *    type: 'pdf'
 *  }
 * ];
 *
 * @param {any} props
 * @public
 */

function AttachmentPreviewer(props) {
  const {
    showPreviewer,
    closePreviwer,
    list = [],
    selectedIndex,
    token
  } = props;

  /**
   * Handles file download using fetch when token is present
   * @param {Object} attachment - The attachment object to download
   */
  const handleDownload = async (attachment) => {
    if (!attachment) return;

    try {
      const url =
        attachment.type === 'superpdf'
          ? attachment?.downloadUrl || ''
          : attachment?.url || '';

      if (!url) return;

      // First request - might get a redirect
      const response = await fetch(url, {
        headers: {
          ...getHeaderValue(),
          authorization: `Bearer nl-${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.status}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = attachment?.name || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Optionally add error handling UI here
    }
  };
  const [attachmentList, setAttachment] = useState(list);
  const [showModal, setModalVisibility] = useState(showPreviewer);
  const [attachmentIndex, setIndex] = useState(selectedIndex || 0);

  /**
   *
   * Watching modal visibility from parent
   * @public
   */
  useEffect(() => {
    setModalVisibility(showPreviewer);
  }, [showPreviewer]);

  useEffect(() => {
    setAttachment(list);
  }, [list]);

  /**
   *
   * Register event listener for keyup
   * @public
   */

  useEffect(() => {
    window.addEventListener('keyup', upHandler);
    // Remove event listener on cleanup
    return () => {
      window.removeEventListener('keyup', upHandler);
    };
  }, [attachmentIndex]);

  /**
   *
   * This method handles keyboard events
   * @param {object} kepupProps
   * @public
   */

  function upHandler({ key }) {
    if (key === 'ArrowRight') switchAttachments('next');
    else if (key === 'ArrowLeft') switchAttachments('prev');
  }

  /**
   *
   * This method handles switching between list of attachments
   * @param {String} type
   * @public
   */

  function switchAttachments(type) {
    if (type === 'next' && attachmentIndex !== attachmentList.length - 1) {
      setIndex(attachmentIndex + 1);
    } else if (type === 'prev' && attachmentIndex > 0) {
      setIndex(attachmentIndex - 1);
    }
  }

  /**
   *
   * This method maps different component type
   * @param {String} type
   * @param {String} url
   * @public
   */

  function componentMap({ type = '', url = '', name }) {
    switch (type || '') {
      // case 'pdf':
      //   return <PDFViewer url={url} />;
      case 'png':
      case 'jpeg':
      case 'jpg':
      case 'JPG':
      case 'JPEG':
      case 'PNG':
        return <ImageViewer url={url} token={token} />;
      // case 'pdf':
      //   return <SuperPdfViewer url={url} />;
      case 'superpdf':
        return (
          <div className="h85p full-width">
            <SuperPdfViewer url={url} hideToolbar token={token} />
          </div>
        );
      default:
        return (
          <DefaultViewer
            name={name}
            url={url}
            token={token}
            handleDownload={handleDownload}
          />
        );
    }
  }

  return (
    <CustomModal open={showModal} onClose={closePreviwer}>
      <PreviewWrapper>
        <PreviewHeader className="flex center fx-sb">
          <label className="title">
            {attachmentList[attachmentIndex]?.name || ''}
          </label>
          <div className="flex center">
            <div className="icons mr-20">
              {token ? (
                <div
                  className="ptr"
                  onClick={() =>
                    handleDownload(attachmentList[attachmentIndex])
                  }
                >
                  <SVGIcon name="Download" color="#fff" />
                </div>
              ) : (
                <Link
                  url={
                    attachmentList[attachmentIndex]?.type === 'superpdf'
                      ? attachmentList[attachmentIndex]?.downloadUrl || ''
                      : attachmentList[attachmentIndex]?.url || ''
                  }
                  removeUnderline
                >
                  <SVGIcon name="Download" color="#fff" />
                </Link>
              )}
            </div>
            <div className="icons ptr">
              <div onClick={closePreviwer}>
                <SVGIcon name="Close" color="#fff" />
              </div>
            </div>
          </div>
        </PreviewHeader>
        <PreviewerContainer
          prev={attachmentIndex > 0}
          next={attachmentIndex !== attachmentList.length - 1}
          className="full-height"
        >
          <div className="prev-button-container">
            <div
              className="nav-buttons"
              onClick={() => switchAttachments('prev')}
            >
              <SVGIcon name="ArrowLeft" size="xl" color="white" />
            </div>
          </div>
          <div className="content full-height full-width">
            {componentMap(attachmentList[attachmentIndex] || '')}
          </div>
          <div className="next-button-container">
            <div
              className="nav-buttons"
              onClick={() => switchAttachments('next')}
            >
              <SVGIcon name="ArrowRight" size="xl" color="white" />
            </div>
          </div>
        </PreviewerContainer>
      </PreviewWrapper>
    </CustomModal>
  );
}

export default AttachmentPreviewer;

const PreviewWrapper = styled.div`
  background-color: #1b1b1f;
  width: 90vw;
  height: 90vh;
  border-radius: 20px;
`;

const PreviewerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  /* height: 80%; */
  border-radius: 20px;

  .nav-buttons {
    margin: 0px 10px;
    width: 63px;
    background: #000;
    border: none;
    height: 56px;
    cursor: pointer;
    align-items: center;
    display: flex;
    justify-content: center;
    border-radius: 5px;
  }
  .content {
  }
  .prev-button-container {
    visibility: ${(props) => `${props.prev ? 'visible' : 'hidden'}`};
  }
  .next-button-container {
    visibility: ${(props) => `${props.next ? 'visible' : 'hidden'}`};
  }
`;

const PreviewHeader = styled.div`
  padding: 20px;

  .title {
    color: #fff;
    font-size: 20px;
  }

  .icon {
    cursor: pointer;
    height: 25px;
    width: 25px;
  }
`;
